import { LEGACY_IMPORTED_LABEL, LEGACY_IMPORTED_SOURCE } from './legacyImportedMessages';
// BL-177 Step 2d (sections 2, 3, 7, 8): what a Communications screen SHOWS for a thread, a message and the composer,
// worked out in one place so the list, the thread header, the bubbles and the composer agree.
//
// Colours come only from src/lib/colorMeaning.js (one meaning per colour, always with words) and every phone number
// is formatted by src/lib/phone.js. Pure functions: no React, no Supabase.

import { DELIVERY_TONES, TAG_TONES, flagMeaning } from './colorMeaning';
import { formatPhone, isAlphaSender, isShortCode } from './phone';
import { GROUP_FALLBACK_THREAD, groupParticipants } from './communicationRecipients';
import { isConversationClosed, normalizeSessionFlagStatus } from './communicationsFlags';
import { contactInitials } from './communicationsThread';

const GROUP_THREAD_PREFIX = 'group:';

function text(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function sessionOf(thread) {
    return thread?.session || thread || {};
}

/** An imported provider group conversation ("group:+1...,+1..." or the catch-all "group-text" thread). */
export function isGroupThread(threadOrSession) {
    const phone = text(sessionOf(threadOrSession).contact_phone);
    return phone.startsWith(GROUP_THREAD_PREFIX) || phone === GROUP_FALLBACK_THREAD;
}

/** How many people are on a group thread, or 0 when unknown. */
export function groupSize(threadOrSession) {
    const phone = text(sessionOf(threadOrSession).contact_phone);
    if (!phone.startsWith(GROUP_THREAD_PREFIX)) return 0;
    return phone.slice(GROUP_THREAD_PREFIX.length).split(/[,;|]/).map((part) => part.trim()).filter(Boolean).length;
}

/**
 * BL-177 Step 2e (6b): who is who on a group. `contacts` = CRM contacts ({ name, phones[] }); returns { "+1404...": "Name" }
 * for every person on the group who is a known contact. People without a contact are shown by their formatted number.
 */
export function participantNameMap(phones = [], contacts = []) {
    const digits = (value) => String(value || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
    const byDigits = new Map();
    (Array.isArray(contacts) ? contacts : []).forEach((contact) => {
        const name = text(contact?.name);
        if (!name) return;
        (Array.isArray(contact?.phones) ? contact.phones : []).forEach((phone) => {
            const key = digits(typeof phone === 'string' ? phone : phone?.number ?? phone?.phone ?? phone?.value);
            if (key && !byDigits.has(key)) byDigits.set(key, name);
        });
    });
    const names = {};
    phones.forEach((phone) => { const name = byDigits.get(digits(phone)); if (name) names[phone] = name; });
    return names;
}

/** A group member's label: their contact name, else their formatted number. */
export function participantLabel(phone, names = {}) {
    const known = text(names?.[phone]);
    return known || (phone ? formatPhone(phone) : '');
}

/** "Lynette B., Mary M. + you": the people on a group, then "+ you". Long groups: the first three, then "+ N more". */
export function groupTitle(session, names = {}) {
    const people = groupParticipants(text(session?.contact_phone)).map((phone) => participantLabel(phone, names));
    if (people.length === 0) return '';
    if (String(session?.external_thread_id || '').startsWith('conversations:')) return `${people.slice(0, 2).map((name) => name.split(' ')[0]).join(', ')}${people.length > 2 ? ` +${people.length - 2}` : ''}`;
    const shown = people.length > 4 ? [...people.slice(0, 3), `+ ${people.length - 3} more`] : people;
    return `${shown.join(', ')} + you`;
}

export function isShortCodeThread(threadOrSession) {
    return isShortCode(text(sessionOf(threadOrSession).contact_phone));
}

/** Short codes and name-style senders (AMAZON) can't be answered. */
export function isNoReplySender(threadOrSession) {
    const phone = text(sessionOf(threadOrSession).contact_phone);
    return Boolean(phone) && (isShortCode(phone) || isAlphaSender(phone));
}

// The DB marks an inbound conversation that arrived outside business hours. Column per the Step 2d DB contract;
// the camelCase spelling covers rows already mapped by a loader.
export function isAfterHoursThread(thread) {
    const session = sessionOf(thread);
    return thread?.after_hours === true || thread?.afterHours === true || session.after_hours === true;
}

function looksLikePhone(value) {
    return /^\+?[\d\s().-]{3,}$/.test(text(value));
}

/** The name a row/header shows: the contact's name, else the FORMATTED number (short codes as-is). */
export function threadDisplayName(thread, { participantNames = null } = {}) {
    const session = sessionOf(thread);
    if (isGroupThread(session)) {
        if (!participantNames && String(session.external_thread_id || '').startsWith('conversations:') && thread?.name && thread.name !== session.contact_phone) return thread.name;
        if (participantNames) {
            const title = groupTitle(session, participantNames);
            if (title) return title;
        }
        const size = groupSize(session);
        return size > 0 ? `Group text · ${size} people` : 'Group text';
    }
    const name = text(thread?.name);
    const phone = text(session.contact_phone);
    if (!name || name === phone || looksLikePhone(name)) {
        const number = name && looksLikePhone(name) ? name : phone;
        return number ? formatPhone(number) : (name || 'Unknown contact');
    }
    return name;
}

/** The round avatar: initials for a name, "+49" for an unnamed international number, "#" for a short code, "G" for a group. */
export function threadAvatarLabel(thread) {
    const session = sessionOf(thread);
    if (isGroupThread(session)) return 'G';
    if (isShortCodeThread(session)) return '#';
    const name = text(thread?.name);
    const phone = text(session.contact_phone);
    if (!name || name === phone || looksLikePhone(name)) {
        const match = /^\+(\d{1,3})/.exec(formatPhone(name && looksLikePhone(name) ? name : phone));
        return match ? `+${match[1]}` : '#';
    }
    return contactInitials(name);
}

/** "+1 (404) 555-0143 · The Ride" — the line under the name in the thread header. */
export function threadHeaderSubtitle({ session, companyName = '', showAddress = true } = {}) {
    const phone = text(session?.contact_phone);
    let address = '';
    if (isGroupThread(session)) address = '';
    else if (showAddress && phone) address = isNoReplySender(session) ? `${phone} · can't reply` : formatPhone(phone);
    else if (showAddress) address = text(session?.contact_email);
    return [address, text(companyName)].filter(Boolean).join(' · ');
}

/** The colour-system flag for a thread ({ key, tone, label }) or null. Org-custom flag colours are not used. */
export function threadFlag(threadOrSession) {
    const session = sessionOf(threadOrSession);
    const key = isConversationClosed(session) ? 'closed' : normalizeSessionFlagStatus(session);
    const meaning = flagMeaning(key);
    return meaning ? { key, ...meaning } : null;
}

function scheduledLabel(thread) {
    const session = sessionOf(thread);
    // Step 3 fills these; until then a thread simply has no Scheduled tag.
    const at = thread?.scheduledAt || thread?.scheduled_at || session.next_scheduled_at || null;
    if (!at && thread?.scheduled !== true) return '';
    const date = at ? new Date(at) : null;
    if (!date || Number.isNaN(date.getTime())) return TAG_TONES.scheduled.label;
    const when = date.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' }).replace(',', '');
    return `${TAG_TONES.scheduled.label} · ${when}`;
}

/** Row tags in board order: After hours, Group text, Short code · can't reply, Scheduled. */
export function threadTags(thread) {
    const tags = [];
    if (isAfterHoursThread(thread)) tags.push({ key: 'after_hours', ...TAG_TONES.after_hours });
    if (isGroupThread(thread)) tags.push({ key: 'group_text', ...TAG_TONES.group_text });
    if (isShortCodeThread(thread)) tags.push({ key: 'short_code', tone: TAG_TONES.short_code.tone, label: `${TAG_TONES.short_code.label} · can't reply` });
    const scheduled = scheduledLabel(thread);
    if (scheduled) tags.push({ key: 'scheduled', tone: TAG_TONES.scheduled.tone, label: scheduled });
    return tags;
}

/** Which delivery state a message shows (only ever on the message): not_delivered / unconfirmed / scheduled / null. */
export function messageDeliveryState(message) {
    if (!message || message.direction === 'inbound') return null;
    const status = text(message.provider_status).toLowerCase();
    if (message.scheduled_cancelled === true) return 'cancelled';
    if (message.scheduled === true || status === 'scheduled') return 'scheduled';
    if (status === 'unconfirmed') return 'unconfirmed';
    if (status === 'failed' || status === 'undelivered') return 'not_delivered';
    return null;
}

export function deliveryMeaning(state) {
    return DELIVERY_TONES[state] || null;
}

/** "7:42 AM" */
export function formatMessageTime(at) {
    if (at === null || at === undefined || at === '') return '';
    const date = new Date(at);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function zonedDateParts(date, timeZone) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
    if (timeZone) options.timeZone = timeZone;
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
    return Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
}

function formattedMessageTime(date, timeZone) {
    return new Intl.DateTimeFormat('en-US', {
        ...(timeZone ? { timeZone } : {}),
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}

/** Today: "4:51 PM"; yesterday: "Yesterday 4:51 PM"; then a year-aware date and time. */
export function formatThreadActivityTimestamp(value, now = new Date(), timeZone = '') {
    return formatActivityTimestamp(value, now, timeZone);
}

/** A complete date and time for the conversation footer, always on the Eastern clock. */
export function formatEasternTimestamp(value, now = new Date()) {
    if (value === null || value === undefined || value === '') return '';
    const date = new Date(value);
    const current = new Date(now);
    if (Number.isNaN(date.getTime()) || Number.isNaN(current.getTime())) return '';
    const timeZone = 'America/New_York';
    const dateParts = zonedDateParts(date, timeZone);
    const currentParts = zonedDateParts(current, timeZone);
    const time = formattedMessageTime(date, timeZone);
    if (dateParts.year === currentParts.year) {
        const month = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short' }).format(date);
        return `${month} ${dateParts.day} ${time}`;
    }
    return `${String(dateParts.month).padStart(2, '0')}/${String(dateParts.day).padStart(2, '0')}/${String(dateParts.year).slice(-2)} ${time}`;
}

function formatActivityTimestamp(value, now, timeZone = '') {
    if (value === null || value === undefined || value === '') return '';
    const date = new Date(value);
    const current = new Date(now);
    if (Number.isNaN(date.getTime()) || Number.isNaN(current.getTime())) return '';
    const dateParts = zonedDateParts(date, timeZone);
    const currentParts = zonedDateParts(current, timeZone);
    const day = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day);
    const today = Date.UTC(currentParts.year, currentParts.month - 1, currentParts.day);
    const dayDifference = Math.round((today - day) / 86400000);
    const time = formattedMessageTime(date, timeZone);
    if (dayDifference === 0) return time;
    if (dayDifference === 1) return `Yesterday ${time}`;
    if (dateParts.year === currentParts.year) {
        const month = new Intl.DateTimeFormat('en-US', {
            ...(timeZone ? { timeZone } : {}),
            month: 'short',
        }).format(date);
        return `${month} ${dateParts.day} ${time}`;
    }
    const shortYear = String(dateParts.year).slice(-2);
    return `${String(dateParts.month).padStart(2, '0')}/${String(dateParts.day).padStart(2, '0')}/${shortYear} ${time}`;
}

/**
 * Who a text of ours came from (BL-177 Step 2e, final rule). Sent from IntelAgent: the staff member's name (every IntelAgent
 * send records who sent it). Sent any other way (the imported provider app, an API, anything else the imported provider relays): "the imported provider app",
 * because the webhook never says who or how. A text of ours that is not an imported provider text and has no name is a plain "Staff".
 */
export function outboundSenderLabel(message, staffNames = {}) {
    if (message?.role === 'assistant') return 'AI Assistant';
    const fromIntelAgent = Boolean(message?.staffUserId || message?.clientRef || message?.pending);
    if (!fromIntelAgent && message?.messageSource === LEGACY_IMPORTED_SOURCE) return LEGACY_IMPORTED_LABEL;
    return text(message?.staffUserId ? staffNames[message.staffUserId] : '') || text(message?.staffName) || 'Staff';
}

/**
 * The grey line under a bubble, as parts so only the time renders dim:
 *   ours, from IntelAgent:  { who: 'Val Whitney',    time: '9:39 PM' }
 *   ours, any other way:    { who: 'the imported provider app',  time: '9:39 PM' }
 *   theirs, one-on-one:     { who: '',               time: '9:40 PM' }
 *   theirs, in a group:     { who: 'Lynette B.',     time: '9:41 PM' }
 * No channel words ("via an imported provider"): the channel already shows on the conversation. A problem state (Not delivered,
 * Unconfirmed, Scheduled) has its own chip under the bubble; the plain progress word (Queued / Delivered / Read) rides at the
 * end of the line of the conversation's latest outbound message only (`showProgress`).
 */
export function bubbleMeta(message, { staffNames = {}, participantNames = {}, showProgress = false } = {}) {
    const outbound = message?.direction === 'outbound';
    let who = '';
    // In a group, every incoming bubble names the person who sent it (contact name, else formatted number).
    if (!outbound && text(message?.senderPhone)) who = participantLabel(message.senderPhone, participantNames);
    if (outbound) who = outboundSenderLabel(message, staffNames);
    // Problem states have their own chip. The plain progress word (Queued / Delivered / Read) shows ONLY on the most recent
    // outbound message of the conversation (the caller says which: showProgress), like iPhone Messages.
    const status = outbound && showProgress ? text(message?.provider_status).toLowerCase() : '';
    return { who, time: message?.pending ? 'Sending…' : formatMessageTime(message?.at), status: PROGRESS_LABELS[status] || '' };
}

const PROGRESS_LABELS = Object.freeze({ queued: 'Queued', sent: 'Sent', delivered: 'Delivered', read: 'Read' });


/** "from +1 (404) 594-2667 · The Ride · via an imported provider" (provider part optional). */
export function composerSenderLine({ number = '', companyName = '', provider = '' } = {}) {
    const formatted = number ? formatPhone(number) : '';
    const parts = [formatted, text(companyName), provider ? `via ${provider}` : ''].filter(Boolean);
    return parts.length ? `from ${parts.join(' · ')}` : '';
}
